/**
 * 「换进去」确认层(W4 批,2026-09-13)—— 幕二导演交代卡「这次这 N 条不讲」区,
 * 点某条灰 chip 的「换进去」弹出的知情确认。
 *
 * ═══ 为什么要先弹一层再重编排 ═══════════════════════════════════════════════════
 * 这个时长档装得下的卖点数是固定的(= 当前选中的那 M 条)—— 换进一条就必须先请出一条,
 * 不是简单的"多讲一条"。用户得先看清自己在拿哪条换、编排费要再花一次,才叫知情同意。
 * 确认后走**与摘要条「重新编排」同一条路**(guardedCompose → triggerComposeJob),
 * 不新造一条编排通道 —— 由 `VideoDirectorBriefCard` 的 `onConfirm` 回调接到页面那条路上。
 */
import { useEffect, useState } from "react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";
import { factText } from "../../model/directorBrief";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 被换入的那条 fact id(unused 区点了「换进去」的那条);null = 没有目标,弹窗不该开。 */
  incomingFid: string | null;
  /** 当前选中的卖点(用户要点一个让位)。 */
  selected: string[];
  factTexts?: Record<string, string> | null;
  /** 这次编排估的成片总秒数;缺省时提示语退化成不报数字,不许瞎编。 */
  estimatedDurationSec?: number | null;
  /** 用户选定「让哪条让位」后点确认。 */
  onConfirm: (releasedFid: string) => void;
}

export function VideoFactSwapDialog({
  open,
  onOpenChange,
  incomingFid,
  selected,
  factTexts,
  estimatedDurationSec,
  onConfirm,
}: Props) {
  const [releaseFid, setReleaseFid] = useState<string | null>(null);

  // 每次换目标 / 重新打开都清掉上一轮选的「让位」,不许残留成误操作。
  useEffect(() => {
    if (open) setReleaseFid(null);
  }, [open, incomingFid]);

  if (!incomingFid) return null;
  const capacity = selected.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>换进「{factText(incomingFid, factTexts)}」</DialogTitle>
          <DialogDescription>
            {estimatedDurationSec != null ? `${estimatedDurationSec} 秒` : "这个时长"}只装得下{" "}
            {capacity} 个卖点,换进一个要请出一个。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <p className="text-2xs text-muted-foreground">点一个让位:</p>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((fid) => (
              <button
                key={fid}
                type="button"
                aria-pressed={releaseFid === fid}
                data-testid={`fact-swap-release-${fid}`}
                onClick={() => setReleaseFid(fid)}
                className={cn(
                  "cursor-pointer rounded-full border px-2.5 py-1 text-xs transition-colors",
                  releaseFid === fid
                    ? "border-destructive bg-destructive/10 text-destructive line-through"
                    : "border-border/60 text-foreground hover:border-destructive/50"
                )}
              >
                {factText(fid, factTexts)}
              </button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" data-testid="fact-swap-cancel">
              先不
            </Button>
          </DialogClose>
          <Button
            type="button"
            data-testid="fact-swap-confirm"
            disabled={!releaseFid}
            onClick={() => releaseFid && onConfirm(releaseFid)}
          >
            按新选料重编排(编排费另计)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
